<template>
	<div>
		<!-- Search Filter -->
		<BaseSearchFilter
			:active="isActive"
			:show="isActive"
			:container-action-class="!isDisabledActions ? 'adjust-column' : ''"
			:body-with-row="false"
		>
			<template #header>
				<CCol>
					<CInput
						v-model="query.keyword"
						placeholder="Search by SKU, product name, order ID or order channel ID"
						@keyup.enter="onInputEnter"
					>
						<template #prepend-content>
							<FontAwesomeIcon :icon="['far', 'search']" />
						</template>
					</CInput>
				</CCol>
			</template>
			<template v-if="!isDisabledActions" #action>
				<CButton
					:disabled="disabledUpdateStatus"
					:color="disabledUpdateStatus ? 'tertiary' : 'secondary'"
					class="transparent mr-3"
					@click="handleClickUpdateStatus"
				>
					Update status
				</CButton>
				<CButton
					:disabled="disabledPrintOrder"
					:color="disabledPrintOrder ? 'tertiary' : 'secondary'"
					class="transparent"
					@click="handleClickPrintOrders"
				>
					<span class="mr-2">
						Print orders
					</span>
					<CIcon name="cil-print" />
				</CButton>
			</template>
			<template #body>
				<CRow>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.status"
							:options="ORDER_STATUS_OPTIONS"
							:label-drop-down="$t('label.status')"
							:placeholder="$t('message.selection')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleStatusChange"
						/>
					</CCol>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.channel"
							:options="channelOptions"
							:label-drop-down="$t('label.channel')"
							:placeholder="$t('message.selection')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleChannelChange"
						/>
					</CCol>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.shipment"
							:options="SHIPMENT_STATUS_OPTIONS"
							:label-drop-down="$t('label.shipment')"
							:placeholder="$t('message.selection')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleShipmentChange"
						/>
					</CCol>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.reconciledAt"
							:options="RECONCILED_ORDER_OPTIONS"
							:label-drop-down="$t('label.reconciled')"
							:placeholder="$t('message.selection')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleReconciledOrder"
						/>
					</CCol>
				</CRow>
				<CRow>
					<CCol md="3">
						<BaseDropDown
							v-model="filter.isSyncedWithThunder"
							:options="SYNCED_WITH_THUNDER_OPTIONS"
							:label-drop-down="$t('label.synced_with_thunder')"
							:placeholder="$t('message.selection')"
							:searchable="false"
							label="name"
							track-by="value"
							@input="handleSyncedToThunder"
						/>
					</CCol>
				</CRow>
			</template>
			<template #footer>
				<CCol md="8">
					{{ $t('message.orders_found', { number: totalCount }) }}
				</CCol>
				<CCol md="4">
					<button @click="onResetClick">
						{{ $t('button.resetFilters') }}
					</button>
				</CCol>
			</template>
		</BaseSearchFilter>

		<BaseTable
			:is-loading="loading"
			:pagination="pagination"
			:fields="mapOrderTableFields"
			:items="dataTable"
			:hide-columns="['id']"
			name-route="OrderSingle"
			striped
			hover
			fixed
			clickable-rows
			class="table-custom"
			@onPaginationClick="handlePageChange"
			@onRowClick="handleRowClick"
		>
			<template #no-items-view>
				<div class="empty-table-element">
					<p class="subtitle">
						{{ $t('message.not_found_order_list') }}
					</p>
				</div>
			</template>
			<template #th-checkbox>
				<CInputCheckbox
					ref="checkbox-all"
					:checked="checkedAll"
					label=""
					custom
					@update:checked="handleCheckedAll"
				/>
			</template>
			<template #status="{ item = '' }">
				<CBadge class="badge-order-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>
			<template #checkbox="{ item }">
				<div v-if="item.show" style="position: relative; z-index: inherit;">
					<CInputCheckbox
						:checked="checkedIds.includes(item.id)"
						:disabled="item.disabled"
						label=""
						custom
						@update:checked="handleChecked($event, item.id)"
					/>
				</div>
			</template>
			<template #payment="{ item = '' }">
				<CBadge class="badge-order-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>
			<template #shipment="{ item = '' }">
				<CBadge class="badge-order-status" :color="item.color">
					{{ item.title }}
				</CBadge>
			</template>
		</BaseTable>

		<!-- Modal Confirm Update -->
		<ModalConfirmUpdateOrderStatus
			ref="modal-confirm-update-status"
			:show.sync="isShowModalConfirmUpdateStatus"
			:title="$t('message.do_you_want_to_update_order_status')"
			:disabled-submit="isLoadingUpdateStatus"
			@onSubmit="handleConfirmUpdateStatus"
			@onCancel="handleCancelUpdateStatus"
		/>
		<!-- End Modal Confirm Update -->
	</div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { randomSearchString, cleanObject, cloneDeep, numberFormat } from '../helpers/utils';
import { getSelectedValue } from '../helpers/dropdown';
import {
	ORDER_TABLE_FIELDS,
	ORDER_COLOR,
	ORDER_TITLE,
	ORDER_STATUS_OPTIONS,
	ORDER_STATUS,
	RECONCILED_ORDER_OPTIONS,
	SYNCED_WITH_THUNDER_OPTIONS,
} from '../enums/order';
import { SHIPMENT_STATUS_OPTIONS, SHIPMENT_STATUS } from '../enums/shipment';
import { ROUTER_NAME } from '../enums/router';

export default {
	name: 'OrderList',

	data() {
		return {
			ORDER_TABLE_FIELDS,
			ORDER_STATUS_OPTIONS,
			SHIPMENT_STATUS_OPTIONS,
			RECONCILED_ORDER_OPTIONS,
			SYNCED_WITH_THUNDER_OPTIONS,
			query: {
				keyword: this.$route.query.keyword || null,
				status: this.$route.query.status || null,
				channel: this.$route.query.channel || null,
				shipment: this.$route.query.shipment || null,
				page: this.$route.query.page || null,
				reconciled_at: this.$route.query.reconciled_at || null,
				is_synced_with_thunder: this.$route.query.is_synced_with_thunder || null,
				r: randomSearchString(),
			},
			filter: {
				status: getSelectedValue(this.$route.query.status, ORDER_STATUS_OPTIONS),
				channel: getSelectedValue(null, this.channelOptions),
				shipment: getSelectedValue(this.$route.query.shipment, SHIPMENT_STATUS_OPTIONS),
				reconciledAt: getSelectedValue(this.$route.query.reconciled_at, RECONCILED_ORDER_OPTIONS),
				isSyncedWithThunder: getSelectedValue(this.$route.query.is_synced_with_thunder, SYNCED_WITH_THUNDER_OPTIONS),
			},
			checkedAll: false,
			checkedIds: [],
			isShowModalConfirmUpdateStatus: false,
			testModel: null,
		};
	},

	computed: {
		...mapState('orderStatusMultipleUpdate', {
			isLoadingUpdateStatus: 'isLoading',
			errorUpdateStatus: 'error',
		}),
		...mapState('orderDocMultiple', {
			isLoadingDocMultiple: 'isLoading',
			errorDocMultiple: 'error',
			dataDocMultiple: 'data',
		}),
		...mapGetters('channelList', {
			channelOptions: 'channelOptions',
		}),
		...mapGetters('me', {
			isAdmin: 'isAdmin',
		}),
		// mapping for data table
		dataTable() {
			return this.$store.state.orderList.data.map((item) => ({
				id: item.id,
				...(
					!this.isDisabledActions
						?
						({
							checkbox: {
								id: item.id,
								disabled: (() => {
									const channelName = item.channel.name || '';
									if (this.checkedIds.length > 0) {
										if (this.currentChannelName !== channelName) {
											return true;
										}
									}

									const isSupportedChannel = [
										'lazada',
										'shopee',
									].includes(channelName.toLowerCase());

									if (!isSupportedChannel) {
										return true;
									}

									return false;
								})(),
								show: (() => {
									const channelName = item.channel.name || '';

									const isSupportedChannel = [
										'lazada',
										'shopee',
									].includes(channelName.toLowerCase());

									if (isSupportedChannel) {
										return true;
									}

									return false;
								})(),
							},
						})
						: null
				),
				order_code: item.orderCode,
				thunder_order_id: item.thunderOrderId,
				synced_with_thunder_at: item.syncedWithThunderAt,
				order_channel_id: item.externalOrderNumber,
				status: {
					title: ORDER_TITLE.ORDER[item.orderStatus],
					color: ORDER_COLOR.ORDER[item.orderStatus],
					original: item.orderStatus,
				},
				reconciled_at: item.reconciledAt,
				created: item.createdAt,
				channel_name: item.channel.name,
				customer_name: `${item.customer.firstName} ${item.customer.lastName}`,
				phone_number: item.customer.phoneNumber,
				email: item.customer.email,
				payment_method: item.paymentMethod.name,
				total: item.total,
				shipment: {
					title: ORDER_TITLE.SHIPMENT[item.shipmentStatus],
					color: ORDER_COLOR.SHIPMENT[item.shipmentStatus],
				},
			}));
		},
		pagination() {
			return this.$store.state.orderList.pagination;
		},
		loading() {
			return this.$store.state.orderList.isLoading;
		},
		totalCount() {
			return numberFormat(this.pagination.total);
		},
		isActive() {
			if (this.filter.status?.value !== null) return true;
			if (this.filter.channel?.value !== null) return true;
			if (this.filter.shipment?.value !== null) return true;
			if (this.filter.reconciledAt?.value !== null) return true;

			return false;
		},
		disabledUpdateStatus() {
			return !this.checkedIds.length;
		},
		disabledPrintOrder() {
			return !this.checkedIds.length || this.isLoadingDocMultiple;
		},
		currentChannelName() {
			if (!this.checkedIds.length) {
				return null;
			}
			const first = this.$store.state.orderList.data.find((row) => row.id === this.checkedIds[0]);
			const currentChannelName = first.channel.name;
			return currentChannelName;
		},
		isDisabledActions() {
			return !this.isAdmin;
		},
		mapOrderTableFields() {
			return this.ORDER_TABLE_FIELDS.filter(({ key }) => {
				if (this.isDisabledActions) {
					return key !== 'checkbox';
				}
				return true;
			});
		},
	},

	async mounted() {
		if (this.channelOptions.length === 0) {
			await this.getChannels();
		}
		const channelCaseClubId = this.channelOptions.length ? this.channelOptions[0].value : null;
		if (channelCaseClubId) {
			this.query.channel = channelCaseClubId;
			this.query.r = null;
			this.updateUrl();
		}
		this.filter.channel = getSelectedValue(this.$route.query.channel
			? parseInt(this.$route.query.channel, 10)
			: null, this.channelOptions);

		await this.fetchData();
	},

	methods: {
		...mapActions('channelList', {
			getChannels: 'getChannels',
		}),
		...mapActions('orderStatusMultipleUpdate', {
			updateOrderStatusMultiple: 'updateOrderStatusMultiple',
		}),
		...mapActions('orderDocMultiple', {
			getOrderDocMultiple: 'getOrderDocMultiple',
		}),
		...mapActions('toast', {
			showToast: 'showToast',
		}),
		async fetchData() {
			await this.$store.dispatch('orderList/getOrders', this.query);
		},
		handleRowClick(id, key) {
			if (key !== 'checkbox-cel') {
				this.$router.push({ name: ROUTER_NAME.ORDER_SINGLE, params: { id } });
			}
		},
		handlePageChange(pageNum) {
			this.query.page = pageNum;
			this.updateUrl();
		},
		onInputEnter(e) {
			this.query.page = null;
			this.query.keyword = e.target.value;
			this.updateUrl();
		},
		handleStatusChange(option) {
			this.query.status = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleCheckedAll(isChecked) {
			this.checkedAll = isChecked;
			if (this.checkedAll) {
				if (this.currentChannelName) {
					this.dataTable.forEach((item) => {
						const channelName = item.channel_name || '';
						if (this.currentChannelName === channelName) {
							if (!this.checkedIds.includes(item.id)) {
								this.checkedIds.push(item.id);
							}
						}
					});
				} else {
					this.checkedIds = [];
					this.dataTable.forEach((item) => {
						const channelName = item.channel_name || '';
						if (['lazada', 'shopee'].includes(channelName.toLowerCase())) {
							if (this.checkedIds.length) {
								if (this.currentChannelName === item.channel_name) {
									this.checkedIds.push(item.id);
								}
							} else {
								this.checkedIds.push(item.id);
							}
						}
					});
				}
			} else {
				this.checkedIds = [];
			}
		},
		handleChecked(isChecked, id) {
			if (isChecked) {
				this.checkedIds.push(id);
			} else {
				const index = this.checkedIds.findIndex((item) => item === id);
				this.checkedIds.splice(index, 1);
			}

			const isCheckAll = (() => {
				if (this.checkedIds.length) {
					return this.dataTable.filter((item) => item.channel_name === this.currentChannelName && !item.checkbox.disabled).length === this.checkedIds.length;
				}
				return this.dataTable.length === this.checkedIds.length;
			})();

			if (isCheckAll) {
				this.checkedAll = true;
				this.$refs['checkbox-all'].$el.querySelector('input').indeterminate = null;
			} else if (!this.checkedIds.length) {
				this.checkedAll = false;
				this.$refs['checkbox-all'].$el.querySelector('input').indeterminate = null;
			} else {
				this.checkedAll = false;
				this.$refs['checkbox-all'].$el.querySelector('input').indeterminate = true;
			}
		},
		onResetClick() {
			this.query.keyword = null;
			this.query.status = null;
			this.query.channel = null;
			this.query.shipment = null;
			this.query.page = null;
			this.query.reconciled_at = null;
			this.query.is_synced_with_thunder = null;
			this.query.r = null;
			this.updateUrl();
		},
		updateUrl() {
			const query = cleanObject(cloneDeep(this.query));
			this.$router.push({ query }).catch(() => {});
		},
		async handleClickUpdateStatus() {
			const statuses = this.checkedIds.reduce((acc, cur) => {
				const item = this.$store.state.orderList.data.find(({ id }) => id === cur);
				acc.push(item.orderStatus);
				return acc;
			}, []);
			const pass = statuses.every((v) => [
				ORDER_STATUS.PREPARING,
			].includes(v));

			if (!pass) {
				this.showToast({
					header: 'Alert',
					content: `Only preparing status can be update`,
					type: 'danger',
				});
			} else {
				this.isShowModalConfirmUpdateStatus = true;
			}
		},
		async handleClickPrintOrders() {
			const statuses = this.checkedIds.reduce((acc, cur) => {
				const item = this.$store.state.orderList.data.find(({ id }) => id === cur);
				acc.push(item.orderStatus);
				acc.push(item.shipmentStatus);
				return acc;
			}, []);

			const pass = statuses.every((v) => [
				ORDER_STATUS.PREPARING,
				ORDER_STATUS.READY_TO_PICK_UP,
				ORDER_STATUS.PICKED_UP,
				ORDER_STATUS.SHIPPED,
				ORDER_STATUS.PARTIALLY_SHIPPED,
				ORDER_STATUS.COMPLETED,
				SHIPMENT_STATUS.READY_TO_SHIP,
				SHIPMENT_STATUS.PARTIALLY_SHIPPED,
				SHIPMENT_STATUS.SHIPPED,
				SHIPMENT_STATUS.DELIVERED,
			].includes(v));

			if (!pass) {
				this.showToast({
					header: 'Alert',
					content: `Some orders cannot be printed due to order status`,
					type: 'danger',
				});
			} else {
				await this.getOrderDocMultiple({
					order_ids: this.checkedIds,
				});

				const error = !!this.errorDocMultiple;

				if (!error) {
					window.open(this.dataDocMultiple.link, '_blank');
				} else {
					this.showToast({
						header: this.$t('toast.unexpectedError'),
						content: this.errorDocMultiple.message,
						type: 'danger',
					});
				}
			}
		},
		handleChannelChange(option) {
			this.query.channel = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		handleShipmentChange(option) {
			this.query.shipment = option?.value ?? null;
			this.query.page = null;
			this.updateUrl();
		},
		async handleConfirmUpdateStatus() {
			await this.updateOrderStatusMultiple({
				order_ids: this.checkedIds,
			});

			const error = !!this.errorUpdateStatus;

			if (!error) {
				this.isShowModalConfirmUpdateStatus = false;

				this.showToast({
					header: this.$t('toast.success'),
					content: this.$t('toast.update_order_status'),
					type: 'success',
				});

				this.checkedIds = [];
				this.checkedAll = false;
				this.$refs['checkbox-all'].$el.querySelector('input').indeterminate = null;
				this.fetchData();
			} else {
				this.showToast({
					header: this.$t('toast.unexpectedError'),
					content: this.errorUpdateStatus.message,
					type: 'danger',
				});
			}
		},
		handleCancelUpdateStatus() {
			this.isShowModalConfirmUpdateStatus = false;
		},
		handleReconciledOrder(option) {
			this.query.reconciled_at = option?.value ?? null;
			this.query.page = null;
			this.updateUrl(this.query);
		},
		handleSyncedToThunder(option) {
			this.query.is_synced_with_thunder = option?.value ?? null;
			this.query.page = null;
			this.updateUrl(this.query);
		},
	},
};
</script>

<style lang="scss" scoped>
	.badge-order-status {
		padding: rem(7) rem(8);
	}

	::v-deep .table-custom {
		.order {
			// order-col, order-cel
			&-col, &-cel {
				min-width: rem(115 + $table-cell-padding);
			}

			// order-cel
			&-cel {
				word-break: unset;

				span {
					margin-right: rem(8);
				}
			}
		}

		.order-channel-id {
			// order-channel-id-col, order-channel-id-cel
			&-col, &-cel {
				min-width: rem(180 + $table-cell-padding);
			}
		}

		.status {
			// status-col
			&-col {
				min-width: rem(150 + $table-cell-padding);
			}
		}

		.created, .group, .reconciled {
			// created-col, group-col, .reconciled-col
			&-col {
				min-width: rem(150 + $table-cell-padding);
			}
		}

		.customer, .email, .channel {
			// customer-col, .email-col
			&-col {
				min-width: rem(180 + $table-cell-padding);
			}
		}

		.payment-method, .shipping-method {
			// .payment-method-col, .shipping-method-col
			&-col {
				min-width: rem(180 + $table-cell-padding);
			}
		}

		.phone-number {
			// phone-number-col
			&-col {
				min-width: rem(180 + $table-cell-padding);
			}
		}

		.total {
			// total-col, total-cel
			&-col, &-cel {
				min-width: rem(100 + $table-cell-padding);
				text-align: right;
				word-break: unset;
			}
		}

		.payment {
			// order-payment-col
			&-col {
				min-width: rem(80 + $table-cell-padding);
			}

			// order-payment-col, order-payment-cel
			&-col, &-cel {
				padding-left: 0;
			}
		}

		.shipment {
			// order-shipment-col
			&-col {
				min-width: rem(160 + $table-cell-padding);
			}

			// order-shipment-col, order-shipment-cel
			&-col, &-cel {
				padding-left: 0;
				padding-right: 0;
			}
		}
	}
</style>
